// Holds the Cloudflare Turnstile token a Supabase auth call has to carry.
//
// The widget (components/TurnstileGate.tsx) pushes every token it produces in
// here; a form takes the current one, or waits for the next, right before it
// calls Supabase. Kept free of React so the waiting logic is unit-testable.

/** What every form shows when no token arrived in time. */
export const TURNSTILE_FAILED_MESSAGE =
  "Verification failed, please try again.";

/** How long a submit waits for the widget before giving up. */
export const TURNSTILE_TOKEN_TIMEOUT_MS = 15_000;

type Waiter = {
  resolve: (token: string) => void;
  timer: ReturnType<typeof setTimeout>;
};

export function createTurnstileTokenStore(
  timeoutMs = TURNSTILE_TOKEN_TIMEOUT_MS,
) {
  let token: string | undefined;
  let waiters: Waiter[] = [];

  return {
    /** The widget solved a challenge: keep the token, wake pending submits. */
    setToken(next: string) {
      token = next;
      const woken = waiters;
      waiters = [];
      for (const waiter of woken) {
        clearTimeout(waiter.timer);
        waiter.resolve(next);
      }
    },
    /**
     * Forget the token (used, expired or errored). Pending submits keep
     * waiting: the widget re-challenges and calls `setToken` again.
     */
    clearToken() {
      token = undefined;
    },
    /** The current token, or the next one if it arrives within `timeoutMs`. */
    getToken(): Promise<string> {
      if (token !== undefined) return Promise.resolve(token);
      return new Promise((resolve, reject) => {
        const waiter: Waiter = {
          resolve,
          timer: setTimeout(() => {
            waiters = waiters.filter((w) => w !== waiter);
            reject(new Error(TURNSTILE_FAILED_MESSAGE));
          }, timeoutMs),
        };
        waiters.push(waiter);
      });
    },
  };
}

export type TurnstileTokenStore = ReturnType<typeof createTurnstileTokenStore>;
